import React, { CSSProperties, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import clsx from 'clsx'
import styled, { keyframes } from 'styled-components'
import Clickable from 'components/Common/Clickable'
import noop from 'lib/function/noop'

const progressAnimation = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0%);
  }
`

const DotsContainer = styled.div`
  display: flex;
  gap: ${rem(4)};
  
  &.position-center {
    justify-content: center;
  }
  
  &.position-start {
    justify-content: start;
  }

  /* Default variant */
  --pagination-dot-bg: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
  --pagination-dot-fill: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  --pagination-dot-border-thickness: 0;

  &.variant-outlined {
  --pagination-dot-bg: ${props => props.theme.palette.neutral.surface.normalBackground};
  --pagination-dot-fill: ${props => props.theme.palette.neutral.surface.normalContrast};
  --pagination-dot-border-thickness: 1px;
  }
`

const DotClickable = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(4)} 0;
`

const Dot = styled.div`
  position: relative;
  height: ${rem(8)};
  width: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: var(--pagination-dot-bg);
  border: var(--pagination-dot-border-thickness) solid ${props => props.theme.palette.neutral.default.one};
  overflow: hidden;
  transform: translateZ(0);
  transition: width 0.3s ease;

  &.active {
    width: ${rem(16)};

    &::after {
      animation: ${progressAnimation} var(--pagination-dot-duration) linear forwards;
      animation-play-state: var(--pagination-dot-play-state, running);
    }
  }

  &::after {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--pagination-dot-fill);
    transform: translateX(-100%);
  }
`

type Variant = 'default' | 'outlined'

interface Props {
  count: number
  activeIndex: number
  /** Duration in milliseconds that each slide is displayed */
  slideDuration: number
  onSelect?: (index: number) => void
  /** Whether the progress animation is paused */
  paused?: boolean
  /** Called with `true` on mouse enter, `false` on mouse leave */
  onHoverChange?: (hovered: boolean) => void
  /** @default 'default' */
  variant?: Variant
  position?: 'start' | 'center'
  className?: string
}

function PaginationDots({
  count,
  activeIndex,
  slideDuration,
  onSelect,
  paused,
  onHoverChange = noop,
  variant = 'default',
  position = 'center',
  className,
}: Props) {
  const intl = useIntl()
  const handlePointerEnter = useCallback<React.PointerEventHandler<HTMLDivElement>>((e) => {
    if (e.pointerType === 'mouse') {
      onHoverChange(true)
    }
  }, [onHoverChange])

  const handlePointerLeave = useCallback<React.PointerEventHandler<HTMLDivElement>>((_e) => {
    onHoverChange(false)
  }, [onHoverChange])

  const style = {
    '--pagination-dot-duration': `${slideDuration}ms`,
    '--pagination-dot-play-state': paused ? 'paused' : 'running',
  } as CSSProperties

  const getSlideAriaLabel = useCallback((index: number) => intl.formatMessage(
    {
      id: 'ugx7FCBcHX',
      defaultMessage: 'Go to slide {index}',
      description: 'Aria label for selecting a specific pagination dot slide',
    },
    { index: index + 1 },
  ), [intl])

  return (
    <DotsContainer
      className={clsx(`variant-${variant}`, `position-${position}`, className)}
      style={style}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      {Array.from({ length: count }, (_, index) => (
        <DotClickable
          key={index}
          onClick={() => onSelect?.(index)}
          aria-label={getSlideAriaLabel(index)}
          aria-current={index === activeIndex ? 'true' : undefined}
        >
          <Dot className={clsx({ active: index === activeIndex })} />
        </DotClickable>
      ))}
    </DotsContainer>
  )
}

export default PaginationDots
